import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Factory, Shield, Award, CheckCircle, Truck, Users, Settings, ArrowRight, Star, Zap, Globe, Package, Clock, ThumbsUp, Target, Layers, Headphones, Cable, Battery, Building2, Handshake, BadgeCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OemManufacturer = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    quantity: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState({ loading: false, success: false, error: '' });

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Set Page Title for SEO
    document.title = 'Best OEM Mobile Accessories Manufacturer in India | Private Label Factory | RSD Accessories';
    
    // Set Meta Description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'RSD Accessories Pvt Ltd is the best OEM mobile accessories manufacturer in India. Premium quality private label factory for data cables, chargers, earbuds, neckbands. Bulk orders, custom branding, pan-India delivery. Contact: 9857300030 / 9958559161';
    
    // Set Meta Keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.name = 'keywords';
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.content = 'OEM mobile accessories manufacturer india, private label accessories factory, bulk mobile accessories supplier, white label charger manufacturer, OEM data cable factory, custom branding accessories, mobile accessories wholesale india, earbuds manufacturer india, neckband manufacturer, OEM TWS earbuds, private label electronics india';
    
    // Set Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = 'https://rsdaccessories.com/oem-mobile-accessories-manufacturer-in-india';
    
    // Set Open Graph Tags
    const ogTags = {
      'og:title': 'Best OEM Mobile Accessories Manufacturer in India | RSD Accessories Pvt Ltd',
      'og:description': 'Premium quality OEM mobile accessories manufacturer in India. Private label factory for data cables, chargers, earbuds, neckbands. Bulk orders with custom branding.',
      'og:type': 'website',
      'og:url': 'https://rsdaccessories.com/oem-mobile-accessories-manufacturer-in-india',
      'og:image': '/images/site/site_36.jpg',
      'og:site_name': 'RSD Accessories',
      'og:locale': 'en_IN'
    };
    
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
    
    // Set Twitter Card Tags
    const twitterTags = {
      'twitter:card': 'summary_large_image',
      'twitter:title': 'Best OEM Mobile Accessories Manufacturer in India | RSD Accessories',
      'twitter:description': 'Premium quality OEM private label factory for mobile accessories. Data cables, chargers, earbuds, neckbands. Bulk orders with custom branding.',
      'twitter:image': '/images/site/site_36.jpg'
    };
    
    Object.entries(twitterTags).forEach(([name, content]) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.name = name;
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
    
    // Add Schema Markup for Local Business
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.id = 'oem-manufacturer-schema';
    schemaScript.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "RSD Accessories Pvt Ltd - Best OEM Mobile Accessories Manufacturer in India",
      "description": "RSD Accessories Pvt Ltd is the leading OEM mobile accessories manufacturer in India. Premium quality private label factory for data cables, chargers, earbuds, neckbands with custom branding and bulk order solutions.",
      "url": "https://rsdaccessories.com/oem-mobile-accessories-manufacturer-in-india",
      "logo": "https://rsdaccessories.com/logo.png",
      "image": "/images/site/site_36.jpg",
      "telephone": ["+91-9857300030", "+91-9958559161"],
      "email": "contact@rsdaccessories.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "B-08, Sector 57",
        "addressLocality": "Noida",
        "addressRegion": "Uttar Pradesh",
        "postalCode": "201301",
        "addressCountry": "IN"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "28.6081",
        "longitude": "77.3649"
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        "opens": "09:00",
        "closes": "19:00"
      },
      "priceRange": "$$",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": "650"
      },
      "sameAs": [
        "https://www.facebook.com/rsdaccessories",
        "https://www.instagram.com/rsdaccessories",
        "https://www.linkedin.com/company/rsdaccessories"
      ],
      "areaServed": {
        "@type": "Country",
        "name": "India"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "OEM Mobile Accessories",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Product",
              "name": "OEM Data Cables",
              "category": "Mobile Accessories"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Product",
              "name": "OEM Mobile Chargers",
              "category": "Mobile Accessories"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Product",
              "name": "OEM TWS Earbuds",
              "category": "Mobile Accessories"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Product",
              "name": "OEM Neckbands",
              "category": "Mobile Accessories"
            }
          }
        ]
      }
    });
    
    // Remove existing schema if present
    const existingSchema = document.getElementById('oem-manufacturer-schema');
    if (existingSchema) {
      existingSchema.remove();
    }
    document.head.appendChild(schemaScript);
    
    // Cleanup on unmount
    return () => {
      const schema = document.getElementById('oem-manufacturer-schema');
      if (schema) {
        schema.remove();
      }
    };
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ loading: true, success: false, error: '' });
    
    try {
      const response = await fetch(`${API_URL}/api/corporate-inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        setFormStatus({ loading: false, success: true, error: '' });
        setFormData({ name: '', company: '', email: '', phone: '', quantity: '', message: '' });
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      setFormStatus({ loading: false, success: false, error: 'Failed to submit. Please try again or call us directly.' });
    }
  };

  // Product images from user
  const productImages = [
    {
      url: '/images/site/site_42.jpg',
      alt: 'OEM Fast Charger with Type-C Cable - Premium Mobile Accessories Manufacturer India',
      title: 'Fast Charger + Cable Combo'
    },
    {
      url: '/images/site/site_36.jpg',
      alt: 'RSD DT-12 Braided Data Cable 45W Fast Sync - OEM Cable Manufacturer',
      title: 'DT-12 Braided Cable 45W'
    },
    {
      url: '/images/site/site_41.jpg',
      alt: 'Premium 45W Gold Plated USB Cable - Private Label Accessories Factory',
      title: 'Premium 45W Gold Cable'
    },
    {
      url: '/images/site/site_35.jpg',
      alt: 'RSD Professional Neckband 45 Hours Battery - OEM Neckband Manufacturer India',
      title: 'Professional Neckband 45H'
    },
    {
      url: '/images/site/site_40.jpg',
      alt: 'Premium Blue Braided Data Cable - Bulk Mobile Accessories Supplier',
      title: 'Premium Blue Braided Cable'
    }
  ];

  const productCategories = [
    { 
      icon: Cable, 
      name: 'Data Cables', 
      description: 'Type-C, Micro USB, Lightning, Braided Nylon, Silicone, 3-in-1 cables with 45W-100W fast charging support',
      moq: 'MOQ: 1000 pcs'
    },
    { 
      icon: Battery, 
      name: 'Mobile Chargers', 
      description: '15W to 120W adapters, PD chargers, VOOC/WARP/DASH compatible, dual port and single port options',
      moq: 'MOQ: 500 pcs'
    },
    { 
      icon: Headphones, 
      name: 'TWS Earbuds', 
      description: 'True wireless earbuds with ANC, ENC, 40-100 hours playtime, premium sound quality',
      moq: 'MOQ: 500 pcs'
    },
    { 
      icon: Headphones, 
      name: 'Neckbands', 
      description: 'Bluetooth neckbands with 45+ hours battery, magnetic earbuds, IPX5 water resistant',
      moq: 'MOQ: 500 pcs'
    },
    { 
      icon: Zap, 
      name: 'Car Chargers', 
      description: '65W QC+PD car chargers, dual port, metal body, bullet design with safety features',
      moq: 'MOQ: 500 pcs'
    },
    { 
      icon: Headphones, 
      name: 'Wired Earphones', 
      description: 'Type-C and 3.5mm wired earphones with extra bass, inline controls, HD microphone',
      moq: 'MOQ: 1000 pcs'
    }
  ];

  const whyChooseUs = [
    { icon: Factory, title: 'State-of-the-Art Manufacturing', description: 'Modern factory in Noida with German automated machinery, producing 3L+ units monthly with consistent premium quality' },
    { icon: Settings, title: 'Complete OEM/ODM Solutions', description: 'Full customization options - your branding, packaging design, product specifications, and color variants as per your requirements' },
    { icon: Shield, title: 'Quality Assurance', description: 'ISO 9001:2015 certified with 12-point quality testing. Every product tested for durability, conductivity, and safety compliance' },
    { icon: Truck, title: 'Pan-India Delivery', description: 'Efficient logistics network ensuring timely delivery across all Indian states. International shipping available for export orders' },
    { icon: Award, title: 'Competitive Pricing', description: 'Direct factory pricing with no middlemen. Best rates for bulk orders with flexible payment terms for trusted partners' },
    { icon: Handshake, title: 'Dedicated Support', description: 'Dedicated account manager for each OEM partner. Quick response, sample support, and after-sales assistance' }
  ];

  const oemBenefits = [
    'Custom logo and branding on products',
    'Customized packaging design',
    'Product specification customization',
    'Multiple color and variant options',
    'Quality certification included',
    'Free sampling for bulk orders',
    'Flexible MOQ for new partners',
    'Fast turnaround time',
    'Pan-India and export delivery',
    'Dedicated account manager'
  ];

  const certifications = [
    'ISO 9001:2015 Certified',
    'BIS Certified Products',
    'RoHS Compliant',
    'CE Certified',
    'FCC Approved',
    'Quality Tested'
  ];

  const clientTypes = [
    { icon: Building2, name: 'E-commerce Brands', desc: 'Amazon, Flipkart, Meesho sellers' },
    { icon: Users, name: 'Distributors', desc: 'Regional and national distributors' },
    { icon: Globe, name: 'Export Partners', desc: 'Middle East, Africa, SEA markets' },
    { icon: BadgeCheck, name: 'Corporate Gifting', desc: 'IT companies, startups, events' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px'}}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 py-20 md:py-32 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              {/* Breadcrumb for SEO */}
              <nav className="mb-4 text-sm" aria-label="Breadcrumb">
                <ol className="flex items-center gap-2 text-neutral-400">
                  <li><Link to="/" className="hover:text-[#FF4D00] transition-colors">Home</Link></li>
                  <li>/</li>
                  <li className="text-[#FF4D00]">OEM Mobile Accessories Manufacturer</li>
                </ol>
              </nav>
              <div className="inline-flex items-center gap-2 bg-[#FF4D00]/20 border border-[#FF4D00]/30 rounded-full px-4 py-2 mb-6">
                <Factory className="w-4 h-4 text-[#FF4D00]" />
                <span className="text-sm text-[#FF4D00] font-medium">Best OEM Factory in India | Made in Noida</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6 leading-tight">
                Best <span className="text-[#FF4D00]">OEM Mobile Accessories</span> Manufacturer in India
              </h1>
              <p className="text-lg md:text-xl text-neutral-300 mb-4 leading-relaxed">
                <strong>RSD Accessories Pvt Ltd</strong> is India's leading <strong>OEM mobile accessories manufacturer</strong>. We offer complete <strong>private label manufacturing</strong> solutions for data cables, chargers, earbuds, neckbands, and car chargers.
              </p>
              <p className="text-base text-neutral-400 mb-8 leading-relaxed">
                Partner with the <strong>best OEM factory in India</strong> for premium quality products with your custom branding. From concept to delivery, we handle everything - product development, custom packaging, quality testing, and pan-India logistics.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="tel:+919857300030">
                  <Button className="bg-[#FF4D00] hover:bg-[#e64500] text-white px-8 py-6 text-lg rounded-full" data-testid="hero-call-btn-1">
                    <Phone className="w-5 h-5 mr-2" />
                    Call: 9857300030
                  </Button>
                </a>
                <a href="tel:+919958559161">
                  <Button variant="outline" className="border-white text-white hover:bg-white hover:text-neutral-900 px-8 py-6 text-lg rounded-full" data-testid="hero-call-btn-2">
                    <Phone className="w-5 h-5 mr-2" />
                    Call: 9958559161
                  </Button>
                </a>
              </div>
              <div className="mt-6">
                <a href="#inquiry-form">
                  <Button variant="link" className="text-[#FF4D00] hover:text-white px-0 text-lg">
                    Get OEM Quote Now
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </a>
              </div>
            </div>
            <div className="relative">
              <img 
                src={productImages[1].url}
                alt="Best OEM Mobile Accessories Manufacturer in India - RSD Accessories Premium Private Label Factory"
                className="w-full max-w-lg mx-auto rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-4 -right-4 bg-[#FF4D00] text-white px-6 py-3 rounded-xl font-bold shadow-lg">
                Private Label OEM Factory
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Banner */}
      <section className="bg-[#FF4D00] py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center text-white">
            <div>
              <p className="text-4xl md:text-5xl font-black">3L+</p>
              <p className="text-sm md:text-base opacity-90">Units/Month Capacity</p>
            </div>
            <div>
              <p className="text-4xl md:text-5xl font-black">500+</p>
              <p className="text-sm md:text-base opacity-90">OEM Partners</p>
            </div>
            <div>
              <p className="text-4xl md:text-5xl font-black">6</p>
              <p className="text-sm md:text-base opacity-90">Product Categories</p>
            </div>
            <div>
              <p className="text-4xl md:text-5xl font-black">15+</p>
              <p className="text-sm md:text-base opacity-90">Countries Served</p>
            </div>
          </div>
        </div>
      </section>

      {/* About OEM Services Section */}
      <section className="py-20 bg-neutral-50" id="about">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-neutral-900">
                Your Trusted <span className="text-[#FF4D00]">OEM Partner</span> for Mobile Accessories
              </h2>
              <p className="text-lg text-neutral-600 mb-6 leading-relaxed">
                <strong>RSD Accessories Pvt Ltd</strong> is a leading <strong>OEM mobile accessories manufacturer in India</strong>, headquartered in Noida, Uttar Pradesh. With our state-of-the-art manufacturing facility equipped with German precision machinery, we produce over <strong>3 lakh units every month</strong> with consistent premium quality.
              </p>
              <p className="text-lg text-neutral-600 mb-6 leading-relaxed">
                As a <strong>private label accessories factory</strong>, we specialize in complete OEM/ODM solutions for brands, e-commerce sellers, distributors, and corporate clients across India and internationally. From product design to custom packaging, quality testing to pan-India delivery - we handle everything.
              </p>
              <p className="text-lg text-neutral-600 mb-6 leading-relaxed">
                Our <strong>OEM mobile accessories</strong> range includes data cables (Type-C, Micro USB, Lightning), mobile chargers (15W to 120W), TWS earbuds, Bluetooth neckbands, car chargers, and wired earphones. All products come with your custom branding and packaging.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-xl shadow-sm border">
                  <p className="text-3xl font-black text-[#FF4D00]">10+</p>
                  <p className="text-neutral-600">Years Experience</p>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-sm border">
                  <p className="text-3xl font-black text-[#FF4D00]">100%</p>
                  <p className="text-neutral-600">Quality Tested</p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img 
                src={productImages[0].url}
                alt="OEM Fast Charger Manufacturing - Private Label Mobile Accessories India"
                className="w-full rounded-xl shadow-lg"
              />
              <img 
                src={productImages[2].url}
                alt="Premium Data Cable OEM Manufacturing - Bulk Accessories Supplier"
                className="w-full rounded-xl shadow-lg mt-8"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Product Categories Section */}
      <section className="py-20 bg-white" id="products">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-900">
              OEM <span className="text-[#FF4D00]">Product Categories</span>
            </h2>
            <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
              As the <strong>best OEM mobile accessories manufacturer in India</strong>, we offer a complete range of products with custom branding and packaging options. All products available for <strong>private label manufacturing</strong>.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {productCategories.map((category, index) => (
              <div key={index} className="group bg-neutral-50 rounded-2xl p-8 hover:bg-gradient-to-br hover:from-[#FF4D00] hover:to-orange-500 transition-all duration-300 border border-neutral-100">
                <div className="w-14 h-14 bg-[#FF4D00] group-hover:bg-white rounded-xl flex items-center justify-center mb-6">
                  <category.icon className="w-7 h-7 text-white group-hover:text-[#FF4D00]" />
                </div>
                <h3 className="text-xl font-bold text-neutral-900 group-hover:text-white mb-3">{category.name}</h3>
                <p className="text-neutral-600 group-hover:text-white/90 mb-4">{category.description}</p>
                <span className="inline-block bg-white/20 group-hover:bg-white/30 text-neutral-700 group-hover:text-white text-sm px-3 py-1 rounded-full font-medium">
                  {category.moq}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Gallery Section */}
      <section className="py-20 bg-neutral-50" id="gallery">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-900">
              Our <span className="text-[#FF4D00]">Premium Products Gallery</span>
            </h2>
            <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
              Explore our range of <strong>OEM mobile accessories</strong> manufactured at our Noida factory. All products available for <strong>private label manufacturing</strong> with your custom branding.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {productImages.map((image, index) => (
              <div key={index} className="group relative aspect-square rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300">
                <img 
                  src={image.url}
                  alt={image.alt}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                  <p className="text-white text-sm font-medium">{image.title}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/shop">
              <Button className="bg-[#FF4D00] hover:bg-[#e64500] text-white px-8 py-6 text-lg rounded-full" data-testid="view-all-products-btn">
                View All Products
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gradient-to-br from-neutral-900 to-neutral-800 text-white" id="why-us">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Partner with <span className="text-[#FF4D00]">RSD Accessories</span>?
            </h2>
            <p className="text-lg text-neutral-300 max-w-3xl mx-auto">
              As the <strong>best OEM mobile accessories manufacturer in India</strong>, we deliver excellence at every step. Here's why 500+ brands trust us for their <strong>private label manufacturing</strong> needs.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-[#FF4D00]/50 transition-all duration-300">
                <div className="w-14 h-14 bg-[#FF4D00] rounded-xl flex items-center justify-center mb-6">
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                <p className="text-neutral-400">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OEM Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-gradient-to-r from-[#FF4D00] to-orange-500 rounded-3xl p-8 md:p-12 text-white">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Complete OEM/ODM Manufacturing Services
                </h2>
                <p className="text-lg mb-6 text-white/90">
                  As the <strong>best OEM mobile accessories manufacturer in India</strong>, we provide end-to-end private label solutions. From product development to delivery, we handle everything:
                </p>
                <ul className="space-y-3">
                  {oemBenefits.map((benefit, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-white flex-shrink-0" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="text-center">
                <img 
                  src={productImages[3].url}
                  alt="OEM Neckband Manufacturing - Premium Private Label Accessories India"
                  className="w-full max-w-sm mx-auto rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Client Types Section */}
      <section className="py-16 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-900">
              Who We <span className="text-[#FF4D00]">Serve</span>
            </h2>
            <p className="text-lg text-neutral-600">
              Our <strong>OEM mobile accessories</strong> are trusted by various business segments across India
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {clientTypes.map((client, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 text-center shadow-sm border border-neutral-100 hover:shadow-lg transition-shadow">
                <client.icon className="w-12 h-12 mx-auto mb-4 text-[#FF4D00]" />
                <h3 className="font-bold text-neutral-900 mb-2">{client.name}</h3>
                <p className="text-sm text-neutral-600">{client.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-900">
              Quality <span className="text-[#FF4D00]">Certifications</span>
            </h2>
            <p className="text-lg text-neutral-600">
              All our <strong>OEM mobile accessories</strong> meet international quality standards
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4">
            {certifications.map((cert, index) => (
              <div key={index} className="bg-neutral-100 rounded-full px-6 py-3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-[#FF4D00]" />
                <span className="font-medium text-neutral-700">{cert}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OEM Process Section */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-900">
              Our <span className="text-[#FF4D00]">OEM Process</span>
            </h2>
            <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
              Simple and transparent process to start your <strong>private label mobile accessories</strong> business with the <strong>best OEM manufacturer in India</strong>.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Inquiry & Discussion', desc: 'Share your requirements - products, quantity, branding needs. Our team will provide detailed quotation.' },
              { step: '02', title: 'Sample & Approval', desc: 'We create samples with your branding. Approve the design, packaging, and product quality before bulk production.' },
              { step: '03', title: 'Production & QC', desc: 'Bulk manufacturing at our Noida factory. 12-point quality testing on every product before dispatch.' },
              { step: '04', title: 'Delivery & Support', desc: 'Pan-India delivery via trusted logistics. Dedicated account manager for ongoing support and reorders.' }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-sm border border-neutral-100">
                <div className="text-5xl font-black text-[#FF4D00]/20 mb-4">{item.step}</div>
                <h3 className="text-xl font-bold text-neutral-900 mb-2">{item.title}</h3>
                <p className="text-neutral-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inquiry Form Section */}
      <section className="py-20 bg-gradient-to-br from-neutral-900 to-neutral-800 text-white" id="inquiry-form">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Get Your <span className="text-[#FF4D00]">OEM Quote</span> Today
            </h2>
            <p className="text-lg text-neutral-300">
              Fill the form below to start your <strong>private label mobile accessories</strong> journey with India's best OEM manufacturer
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium mb-2">Your Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-neutral-400 focus:outline-none focus:border-[#FF4D00]"
                  placeholder="Enter your name"
                  data-testid="oem-form-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Company Name *</label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-neutral-400 focus:outline-none focus:border-[#FF4D00]"
                  placeholder="Enter company name"
                  data-testid="oem-form-company"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-neutral-400 focus:outline-none focus:border-[#FF4D00]"
                  placeholder="Enter email address"
                  data-testid="oem-form-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-neutral-400 focus:outline-none focus:border-[#FF4D00]"
                  placeholder="Enter phone number"
                  data-testid="oem-form-phone"
                />
              </div>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">Estimated Monthly Quantity *</label>
              <select
                name="quantity"
                value={formData.quantity}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white focus:outline-none focus:border-[#FF4D00]"
                data-testid="oem-form-quantity"
              >
                <option value="" className="bg-neutral-800">Select quantity range</option>
                <option value="500-1000" className="bg-neutral-800">500 - 1,000 units</option>
                <option value="1000-5000" className="bg-neutral-800">1,000 - 5,000 units</option>
                <option value="5000-10000" className="bg-neutral-800">5,000 - 10,000 units</option>
                <option value="10000-50000" className="bg-neutral-800">10,000 - 50,000 units</option>
                <option value="50000+" className="bg-neutral-800">50,000+ units</option>
              </select>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">Requirements & Message</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-neutral-400 focus:outline-none focus:border-[#FF4D00]"
                placeholder="Tell us about your OEM requirements - products, branding needs, target market..."
                data-testid="oem-form-message"
              ></textarea>
            </div>
            
            {formStatus.success && (
              <div className="mb-6 p-4 bg-green-500/20 border border-green-500/30 rounded-xl text-green-400">
                Thank you! Your inquiry has been submitted. Our team will contact you within 24 hours.
              </div>
            )}
            
            {formStatus.error && (
              <div className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-xl text-red-400">
                {formStatus.error}
              </div>
            )}
            
            <Button 
              type="submit" 
              disabled={formStatus.loading}
              className="w-full bg-[#FF4D00] hover:bg-[#e64500] text-white px-8 py-6 text-lg rounded-full font-bold"
              data-testid="oem-form-submit"
            >
              {formStatus.loading ? 'Submitting...' : 'Submit OEM Inquiry'}
            </Button>
          </form>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white" id="contact">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-900">
              Contact <span className="text-[#FF4D00]">OEM Team</span>
            </h2>
            <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
              Get in touch with RSD Accessories - Your trusted <strong>OEM mobile accessories manufacturer in India</strong>. We're ready to serve your business.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <a href="tel:+919857300030" className="bg-neutral-50 rounded-2xl p-8 text-center hover:bg-[#FF4D00] hover:text-white transition-all duration-300 border border-neutral-100 group">
              <div className="w-16 h-16 bg-[#FF4D00] group-hover:bg-white rounded-full flex items-center justify-center mx-auto mb-6">
                <Phone className="w-8 h-8 text-white group-hover:text-[#FF4D00]" />
              </div>
              <h3 className="text-xl font-bold mb-2">Call Us</h3>
              <p className="text-[#FF4D00] group-hover:text-white text-lg font-semibold">9857300030</p>
              <p className="text-[#FF4D00] group-hover:text-white text-lg font-semibold">9958559161</p>
              <p className="text-neutral-400 group-hover:text-white/80 mt-2">Mon-Sat: 9AM - 7PM</p>
            </a>
            
            <a href="mailto:contact@rsdaccessories.com" className="bg-neutral-50 rounded-2xl p-8 text-center hover:bg-[#FF4D00] hover:text-white transition-all duration-300 border border-neutral-100 group">
              <div className="w-16 h-16 bg-[#FF4D00] group-hover:bg-white rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail className="w-8 h-8 text-white group-hover:text-[#FF4D00]" />
              </div>
              <h3 className="text-xl font-bold mb-2">Email Us</h3>
              <p className="text-[#FF4D00] group-hover:text-white text-lg font-semibold">contact@rsdaccessories.com</p>
              <p className="text-neutral-400 group-hover:text-white/80 mt-2">We reply within 24 hours</p>
            </a>
            
            <div className="bg-neutral-50 rounded-2xl p-8 text-center border border-neutral-100">
              <div className="w-16 h-16 bg-[#FF4D00] rounded-full flex items-center justify-center mx-auto mb-6">
                <MapPin className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-neutral-900">Factory Address</h3>
              <p className="text-neutral-600">RSD Accessories Pvt Ltd</p>
              <p className="text-neutral-500 mt-2">B-08, Sector 57,<br />Noida 201301,<br />Uttar Pradesh, India</p>
            </div>
          </div>

          <div className="text-center mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://wa.me/919857300030?text=Hi,%20I'm%20interested%20in%20OEM%20mobile%20accessories%20manufacturing" target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#25D366] hover:bg-[#128C7E] text-white px-8 py-6 text-lg rounded-full" data-testid="whatsapp-oem-btn">
                WhatsApp for OEM Inquiry
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
            <Link to="/become-distributor">
              <Button variant="outline" className="border-[#FF4D00] text-[#FF4D00] hover:bg-[#FF4D00] hover:text-white px-8 py-6 text-lg rounded-full" data-testid="become-distributor-btn">
                Become a Distributor
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="py-16 bg-neutral-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold text-neutral-900 mb-4">Best OEM Mobile Accessories Manufacturer in India - RSD Accessories Pvt Ltd</h2>
            <p className="text-neutral-600 mb-4">
              <strong>RSD Accessories Pvt Ltd</strong> is India's leading <strong>OEM mobile accessories manufacturer</strong>, headquartered in Noida, Uttar Pradesh. With a state-of-the-art manufacturing facility equipped with German precision machinery, we produce over <strong>3 lakh premium quality units every month</strong>. Our <strong>private label factory</strong> serves 500+ brands across India and 15+ countries globally.
            </p>
            
            <h3 className="text-xl font-bold text-neutral-900 mb-3">Complete OEM Mobile Accessories Range</h3>
            <p className="text-neutral-600 mb-4">
              As the <strong>best OEM mobile accessories manufacturer in India</strong>, we offer a comprehensive product range including premium data cables (Type-C, Micro USB, Lightning, braided nylon, silicone cables with 45W-100W fast charging), mobile chargers (15W to 120W adapters, PD chargers, VOOC/WARP/DASH compatible), TWS earbuds with ANC/ENC technology, Bluetooth neckbands with 45+ hours battery, car chargers (65W QC+PD), and wired earphones. All products are available for <strong>private label manufacturing</strong> with your custom branding.
            </p>
            
            <h3 className="text-xl font-bold text-neutral-900 mb-3">Why Choose RSD as Your OEM Partner?</h3>
            <p className="text-neutral-600 mb-4">
              Choosing <strong>RSD Accessories</strong> as your <strong>OEM mobile accessories manufacturer</strong> means partnering with a company that prioritizes quality, reliability, and customer satisfaction. Our ISO 9001:2015 certified factory ensures consistent quality with 12-point testing on every product. We offer complete OEM/ODM solutions - custom logo branding, packaging design, product specifications, color variants, and flexible MOQ for new partners. With competitive factory pricing and pan-India delivery, we help brands maximize their margins while delivering premium products.
            </p>
            
            <h3 className="text-xl font-bold text-neutral-900 mb-3">OEM Services for E-commerce & Distributors</h3>
            <p className="text-neutral-600 mb-4">
              Whether you're an Amazon/Flipkart seller, regional distributor, or building your own brand, our <strong>OEM mobile accessories</strong> solutions are designed to meet your needs. We serve e-commerce brands looking for private label products, distributors seeking bulk accessories at factory prices, export partners targeting Middle East, Africa, and Southeast Asian markets, and corporate clients needing customized gifting solutions. With 10+ years of experience in <strong>mobile accessories manufacturing</strong>, we understand the market and help our partners succeed.
            </p>
            
            <h3 className="text-xl font-bold text-neutral-900 mb-3">Contact Best OEM Mobile Accessories Manufacturer</h3>
            <p className="text-neutral-600">
              Start your <strong>private label mobile accessories</strong> business today with <strong>RSD Accessories Pvt Ltd</strong> - the <strong>best OEM manufacturer in India</strong>. Visit our factory at <strong>B-08, Sector 57, Noida 201301</strong> or call us at <strong>9857300030 / 9958559161</strong>. Our dedicated OEM team is ready to discuss your requirements, provide samples, and help you build a successful mobile accessories brand. Experience the difference of working with India's most trusted <strong>OEM mobile accessories factory</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* Internal Links Section for SEO */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-neutral-900 mb-8 text-center">
            Explore More from <span className="text-[#FF4D00]">RSD Accessories</span>
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/" className="group bg-neutral-50 rounded-2xl p-6 hover:bg-[#FF4D00] transition-all duration-300 border border-neutral-100">
              <h3 className="text-lg font-bold text-neutral-900 group-hover:text-white mb-2">RSD Accessories Home</h3>
              <p className="text-neutral-600 group-hover:text-white/80 text-sm">Visit our homepage to explore all mobile accessories products</p>
            </Link>
            
            <Link to="/data-cable-manufacturer-in-noida" className="group bg-neutral-50 rounded-2xl p-6 hover:bg-[#FF4D00] transition-all duration-300 border border-neutral-100">
              <h3 className="text-lg font-bold text-neutral-900 group-hover:text-white mb-2">Data Cable Manufacturer in Noida</h3>
              <p className="text-neutral-600 group-hover:text-white/80 text-sm">Best data cable OEM manufacturer - Type-C, Micro USB cables</p>
            </Link>
            
            <Link to="/best-mobile-charger-manufacturer-in-noida" className="group bg-neutral-50 rounded-2xl p-6 hover:bg-[#FF4D00] transition-all duration-300 border border-neutral-100">
              <h3 className="text-lg font-bold text-neutral-900 group-hover:text-white mb-2">Mobile Charger Manufacturer in Noida</h3>
              <p className="text-neutral-600 group-hover:text-white/80 text-sm">Best charger OEM factory - 15W to 120W fast chargers</p>
            </Link>
            
            <Link to="/become-distributor" className="group bg-neutral-50 rounded-2xl p-6 hover:bg-[#FF4D00] transition-all duration-300 border border-neutral-100">
              <h3 className="text-lg font-bold text-neutral-900 group-hover:text-white mb-2">Become a Distributor</h3>
              <p className="text-neutral-600 group-hover:text-white/80 text-sm">Partner with the best OEM mobile accessories manufacturer</p>
            </Link>
            
            <Link to="/shop" className="group bg-neutral-50 rounded-2xl p-6 hover:bg-[#FF4D00] transition-all duration-300 border border-neutral-100">
              <h3 className="text-lg font-bold text-neutral-900 group-hover:text-white mb-2">Shop Mobile Accessories Online</h3>
              <p className="text-neutral-600 group-hover:text-white/80 text-sm">Browse our complete range of premium accessories</p>
            </Link>
            
            <Link to="/about" className="group bg-neutral-50 rounded-2xl p-6 hover:bg-[#FF4D00] transition-all duration-300 border border-neutral-100">
              <h3 className="text-lg font-bold text-neutral-900 group-hover:text-white mb-2">About RSD Accessories Factory</h3>
              <p className="text-neutral-600 group-hover:text-white/80 text-sm">Learn about India's leading OEM manufacturer journey</p>
            </Link>
            
            <Link to="/contact" className="group bg-neutral-50 rounded-2xl p-6 hover:bg-[#FF4D00] transition-all duration-300 border border-neutral-100">
              <h3 className="text-lg font-bold text-neutral-900 group-hover:text-white mb-2">Contact OEM Team</h3>
              <p className="text-neutral-600 group-hover:text-white/80 text-sm">Get in touch for bulk and OEM inquiries</p>
            </Link>
            
            <Link to="/warranty-registration" className="group bg-neutral-50 rounded-2xl p-6 hover:bg-[#FF4D00] transition-all duration-300 border border-neutral-100">
              <h3 className="text-lg font-bold text-neutral-900 group-hover:text-white mb-2">Warranty Registration</h3>
              <p className="text-neutral-600 group-hover:text-white/80 text-sm">Register your RSD products</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-[#FF4D00]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Start Your OEM Partnership Today
          </h2>
          <p className="text-lg text-white/90 mb-8">
            Partner with India's best OEM mobile accessories manufacturer for premium quality private label products
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+919857300030">
              <Button className="bg-white text-[#FF4D00] hover:bg-neutral-100 px-8 py-6 text-lg rounded-full font-bold" data-testid="final-cta-call-btn">
                <Phone className="w-5 h-5 mr-2" />
                Call: 9857300030
              </Button>
            </a>
            <a href="#inquiry-form">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#FF4D00] px-8 py-6 text-lg rounded-full font-bold" data-testid="final-cta-inquiry-btn">
                Get OEM Quote
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OemManufacturer;
